/**
 * The gift catalogue's shelves: a section ("La chambre") holding categories ("Veilleuses
 * et lampes"). A product carries one category key; its section follows from it.
 *
 * Keys are stored in `products.category` and appear in URLs, so a key is never renamed:
 * relabel it instead. Categories are broad on purpose (one "Literie", not seven kinds of
 * sheet): a shelf with three products on it is noise in a filter.
 *
 * The baby and toy shelves come first, the grown-ups' after: a Christmas or birthday list
 * holds a novel or a perfume as often as a toy, and with no shelf for them the classifier
 * files them on the nearest baby one.
 */
export const PRODUCT_SECTIONS = {
  sommeil: {
    label: "Le sommeil",
    categories: {
      gigoteuses: "Gigoteuses et nids d'ange",
      lits: "Berceaux, couffins et lits",
      literie: "Literie",
      veilleuses: "Veilleuses et lampes",
      babyphones: "Babyphones",
    },
  },
  chambre: {
    label: "La chambre",
    categories: {
      meubles: "Commodes et meubles",
      rangement: "Rangement",
      decoration: "Décoration",
      mobiles: "Mobiles",
    },
  },
  textiles: {
    label: "Vêtements et textiles",
    categories: {
      vetements: "Vêtements bébé et enfant",
      langes: "Langes",
      bavoirs: "Bavoirs",
    },
  },
  eveil: {
    label: "L'éveil",
    categories: {
      "tapis-eveil": "Tapis d'éveil et portiques",
      doudous: "Doudous et peluches",
      hochets: "Hochets et dentition",
      "premiers-jouets": "Premiers jouets",
      livres: "Livres",
    },
  },
  repas: {
    label: "Le repas",
    categories: {
      biberons: "Biberons et accessoires",
      "preparation-biberons": "Chauffe-biberons et stérilisateurs",
      allaitement: "Allaitement",
      "chaises-hautes": "Chaises hautes et sièges de table",
      "robots-cuiseurs": "Robots cuiseurs et conservation",
      vaisselle: "Vaisselle",
    },
  },
  toilette: {
    label: "Toilette et soins",
    categories: {
      bain: "Bain",
      change: "Change",
      soins: "Soins",
      sucettes: "Sucettes et attaches",
      humidificateurs: "Humidificateurs",
    },
  },
  equipement: {
    label: "Équipement et sécurité",
    categories: {
      transats: "Transats et balancelles",
      parcs: "Parcs",
      "trotteurs-porteurs": "Trotteurs et porteurs",
      securite: "Sécurité de la maison",
    },
  },
  balades: {
    label: "Balades et voyages",
    categories: {
      poussettes: "Poussettes et accessoires",
      chancelieres: "Chancelières et couvertures",
      "sacs-a-langer": "Sacs à langer",
      portage: "Porte-bébés et écharpes",
      "sieges-auto": "Sièges auto",
      "lits-parapluie": "Lits parapluie",
      "accessoires-voyage": "Accessoires de voyage",
    },
  },
  parents: {
    label: "Pour les parents",
    categories: {
      souvenirs: "Livres de naissance et souvenirs",
      maman: "Pour la maman",
    },
  },
  jouets: {
    label: "Les jouets",
    categories: {
      "jeux-de-societe": "Jeux de société",
      construction: "Jeux de construction",
      imitation: "Jeux d'imitation",
      poupees: "Poupées et poupons",
      vehicules: "Véhicules et circuits",
      figurines: "Figurines et mini-mondes",
      "loisirs-creatifs": "Loisirs créatifs",
      musique: "Instruments de musique",
      "jeux-educatifs": "Jeux éducatifs et puzzles",
      "plein-air": "Plein air et sport",
      deguisements: "Déguisements",
      bagagerie: "Cartables, sacs et trousses",
    },
  },
  mode: {
    label: "Mode et bijoux",
    categories: {
      mode: "Vêtements et accessoires",
      chaussures: "Chaussures",
      bijoux: "Bijoux et montres",
    },
  },
  beaute: {
    label: "Beauté",
    categories: {
      parfums: "Parfums",
      "bien-etre": "Beauté et bien-être",
    },
  },
  culture: {
    label: "Culture et high-tech",
    categories: {
      romans: "Romans, BD et mangas",
      "musique-films": "Musique et films",
      "jeux-video": "Jeux vidéo et consoles",
      "high-tech": "High-tech",
    },
  },
  "maison-loisirs": {
    label: "Maison et loisirs",
    categories: {
      maison: "Maison et cuisine",
      sport: "Sport et outdoor",
      experiences: "Expériences et cartes cadeaux",
    },
  },
} as const;

export type ProductSection = keyof typeof PRODUCT_SECTIONS;

/**
 * A category page with fewer products than this is thin content: it stays out of the
 * sitemap and the hub, and asks not to be indexed.
 */
export const CATEGORY_PAGE_MIN_PRODUCTS = 4;

/**
 * The gift guides: one hub per occasion (`/idees-cadeaux-naissance`) and one page per
 * category under it. The paths are public URLs, so they never change. Birth only for now:
 * it is the catalogue's depth, and the audience Listou serves first.
 */
export const GIFT_GUIDES = {
  birth: "/idees-cadeaux-naissance",
} as const;

export type GiftGuideOccasion = keyof typeof GIFT_GUIDES;

type CategoriesOf<S extends ProductSection> = keyof (typeof PRODUCT_SECTIONS)[S]["categories"];
export type ProductCategory = { [S in ProductSection]: CategoriesOf<S> }[ProductSection];

const SECTION_OF = new Map<string, ProductSection>(
  (Object.keys(PRODUCT_SECTIONS) as ProductSection[]).flatMap((section) =>
    Object.keys(PRODUCT_SECTIONS[section].categories).map((category) => [category, section]),
  ),
);

export const PRODUCT_CATEGORIES = [...SECTION_OF.keys()] as ProductCategory[];

export const isProductCategory = (value: string): value is ProductCategory => SECTION_OF.has(value);

export const sectionOf = (category: ProductCategory): ProductSection =>
  SECTION_OF.get(category) as ProductSection;

export const categoryLabel = (category: ProductCategory): string =>
  (PRODUCT_SECTIONS[sectionOf(category)].categories as Record<string, string>)[category] ??
  category;
